import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ShieldCheck, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", imposte_percent: "", ssn_percent: "", buy_price: "", sell_price: "", notes: "", aliases: "", sort_index: "", has_buy_field: false, has_sell_field: false };

// Formattazione EUR italiana (€ 1.234,56)
const fmtEur = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
};

export function CompanyGuaranteesPanel({ companyId, companyName, canWrite = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // 🆕 15/07/2026 — Dialog "Importa da altra compagnia"
  const [copyOpen, setCopyOpen] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [copyForm, setCopyForm] = useState({ source_company_id: "", selectedIds: new Set(), skipDup: true });
  const [copySource, setCopySource] = useState([]); // garanzie della compagnia source
  const [copying, setCopying] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/guarantees`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      name: g.name || "",
      imposte_percent: g.imposte_percent ?? "",
      ssn_percent: g.ssn_percent ?? "",
      buy_price: g.buy_price ?? "",
      sell_price: g.sell_price ?? "",
      notes: g.notes || "",
      aliases: Array.isArray(g.aliases) ? g.aliases.join("\n") : "",
      sort_index: g.sort_index ?? "",
      has_buy_field: !!g.has_buy_field,
      has_sell_field: !!g.has_sell_field,
    });
    setOpen(true);
  };

  const parseNum = (v) => (v === "" || v === null || v === undefined) ? null : parseFloat(v);
  const parseInt10 = (v) => (v === "" || v === null || v === undefined) ? null : parseInt(v, 10);

  const submit = async () => {
    const name = (form.name || "").trim();
    if (!name) { toast.error("Il nome della garanzia è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        imposte_percent: parseNum(form.imposte_percent),
        ssn_percent: parseNum(form.ssn_percent),
        buy_price: parseNum(form.buy_price),
        sell_price: parseNum(form.sell_price),
        notes: (form.notes || "").trim() || null,
        aliases: (form.aliases || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
        sort_index: parseInt10(form.sort_index),
        has_buy_field: !!form.has_buy_field,
        has_sell_field: !!form.has_sell_field,
      };
      if (editing) await api.put(`/companies/${companyId}/guarantees/${editing.id}`, payload);
      else await api.post(`/companies/${companyId}/guarantees`, payload);
      toast.success(editing ? "Garanzia aggiornata" : "Garanzia aggiunta");
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/companies/${companyId}/guarantees/${deleteTarget.id}`);
      toast.success("Garanzia eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 15/07/2026 — Apri dialog "Importa da altra compagnia"
  const openCopyDialog = async () => {
    try {
      const { data } = await api.get("/companies");
      setCompaniesList((data || []).filter((c) => c.id !== companyId));
    } catch (e) {
      setCompaniesList([]);
      toast.error(formatApiError(e));
      return;
    }
    setCopyForm({ source_company_id: "", selectedIds: new Set(), skipDup: true });
    setCopySource([]);
    setCopyOpen(true);
  };

  const loadCopySource = async (sourceId) => {
    setCopyForm((f) => ({ ...f, source_company_id: sourceId, selectedIds: new Set() }));
    if (!sourceId) { setCopySource([]); return; }
    try {
      const { data } = await api.get(`/companies/${sourceId}/guarantees`);
      setCopySource(Array.isArray(data) ? data : []);
    } catch (e) {
      setCopySource([]);
      toast.error(formatApiError(e));
    }
  };

  const runCopy = async () => {
    if (!copyForm.source_company_id) { toast.error("Seleziona la compagnia di origine"); return; }
    setCopying(true);
    try {
      const payload = {
        source_company_id: copyForm.source_company_id,
        skip_duplicates: copyForm.skipDup,
      };
      if (copyForm.selectedIds.size > 0) {
        payload.guarantee_ids = Array.from(copyForm.selectedIds);
      }
      const { data } = await api.post(`/companies/${companyId}/guarantees/copy-from`, payload);
      toast.success(`${data.copied} garanzi${data.copied === 1 ? "a copiata" : "e copiate"}${data.skipped ? ` · ${data.skipped} saltate (duplicati)` : ""}`);
      setCopyOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCopying(false); }
  };

  return (
    <div data-testid={`company-guarantees-${companyId}`} className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <ShieldCheck className="h-4 w-4 text-indigo-600" />
          Garanzie di <span className="text-indigo-700">{companyName}</span>
          <span className="text-xs font-normal text-slate-500">({items.length})</span>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid={`copy-guarantees-${companyId}`}
              size="sm"
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              onClick={openCopyDialog}
              title="Importa garanzie da un'altra compagnia"
            >
              <Copy className="h-4 w-4 mr-1.5" /> Importa da altra compagnia
            </Button>
            <Button data-testid={`add-guarantee-${companyId}`} size="sm" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 w-[60px]" title="Indice di ordinamento (codice interno)">Ord.</th>
              <th className="text-left px-3 py-2">Garanzia</th>
              <th className="text-right px-3 py-2 w-[90px]">Imposte</th>
              <th className="text-right px-3 py-2 w-[90px]">SSN</th>
              <th className="text-right px-3 py-2 w-[110px]">Acquisto</th>
              <th className="text-right px-3 py-2 w-[110px]">Vendita</th>
              <th className="text-right px-3 py-2 w-[90px]">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid={`guarantees-table-${companyId}`}>
            {loading ? (
              <tr><td colSpan="7" className="px-3 py-4 text-center text-xs text-slate-400">Caricamento…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-xs text-slate-400">
                Nessuna garanzia configurata. Clicca <strong>Aggiungi</strong> per inserirne una.
              </td></tr>
            ) : items.map((g) => (
              <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`guarantee-row-${g.id}`}>
                <td className="px-3 py-2 text-slate-500 tabular-nums text-xs">
                  {g.sort_index != null ? g.sort_index : "—"}
                </td>
                <td className="px-3 py-2 font-medium text-slate-800">
                  {g.name}
                  {g.notes && <div className="text-[11px] text-slate-500 font-normal mt-0.5">{g.notes}</div>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtPct(g.imposte_percent)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtPct(g.ssn_percent)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtEur(g.buy_price)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{fmtEur(g.sell_price)}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  {canWrite && (
                    <>
                      <button data-testid={`edit-guarantee-${g.id}`} onClick={() => openEdit(g)} className="text-blue-600 hover:text-blue-800" title="Modifica">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`delete-guarantee-${g.id}`} onClick={() => setDeleteTarget(g)} className="text-red-600 hover:text-red-800" title="Elimina">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog create/edit */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
        <DialogContent className="max-w-lg" data-testid="guarantee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              {editing ? "Modifica garanzia" : "Aggiungi garanzia"}
              <span className="text-xs font-normal text-slate-500">— {companyName}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome garanzia *</Label>
              <Input
                data-testid="guarantee-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. RCA, Furto e Incendio, Kasko"
                autoFocus
              />
            </div>
            <div className="col-span-2">
              <Label>Indice di ordinamento <span className="text-xs text-slate-400 font-normal">(codice interno, es. 10, 20, 30…)</span></Label>
              <Input
                data-testid="guarantee-sort-index"
                type="number"
                min="0"
                step="1"
                value={form.sort_index}
                onChange={(e) => setForm({ ...form, sort_index: e.target.value })}
                placeholder="Es. 10"
              />
            </div>
            <div>
              <Label>Imposte (%)</Label>
              <Input
                data-testid="guarantee-imposte"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.imposte_percent}
                onChange={(e) => setForm({ ...form, imposte_percent: e.target.value })}
                placeholder="Es. 12.5"
              />
            </div>
            <div>
              <Label>SSN (%)</Label>
              <Input
                data-testid="guarantee-ssn"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.ssn_percent}
                onChange={(e) => setForm({ ...form, ssn_percent: e.target.value })}
                placeholder="Es. 10.5"
              />
            </div>
            <div>
              <Label>Acquisto (€)</Label>
              <Input
                data-testid="guarantee-buy"
                type="number"
                step="0.01"
                min="0"
                value={form.buy_price}
                onChange={(e) => setForm({ ...form, buy_price: e.target.value })}
                placeholder="Es. 100.00"
              />
              <label className="flex items-center gap-2 mt-1 text-[11px] text-slate-600 cursor-pointer" title="Se selezionato, in polizza sarà visibile il campo Acquisto per questa garanzia">
                <input
                  type="checkbox"
                  data-testid="guarantee-has-buy-field"
                  className="accent-indigo-600"
                  checked={!!form.has_buy_field}
                  onChange={(e) => setForm({ ...form, has_buy_field: e.target.checked })}
                />
                Mostra campo Acquisto in polizza
              </label>
            </div>
            <div>
              <Label>Vendita (€)</Label>
              <Input
                data-testid="guarantee-sell"
                type="number"
                step="0.01"
                min="0"
                value={form.sell_price}
                onChange={(e) => setForm({ ...form, sell_price: e.target.value })}
                placeholder="Es. 150.00"
              />
              <label className="flex items-center gap-2 mt-1 text-[11px] text-slate-600 cursor-pointer" title="Se selezionato, in polizza sarà visibile il campo Vendita per questa garanzia">
                <input
                  type="checkbox"
                  data-testid="guarantee-has-sell-field"
                  className="accent-indigo-600"
                  checked={!!form.has_sell_field}
                  onChange={(e) => setForm({ ...form, has_sell_field: e.target.checked })}
                />
                Mostra campo Vendita in polizza
              </label>
            </div>
            <div className="col-span-2">
              <Label>Note <span className="text-xs text-slate-400 font-normal">(facoltativo)</span></Label>
              <Input
                data-testid="guarantee-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Note interne"
              />
            </div>
            {/* 🆕 Feb 2026 — Alias/sinonimi: nomi alternativi con cui l'estrazione AI può etichettare la garanzia */}
            <div className="col-span-2">
              <Label>Alias / sinonimi <span className="text-xs text-slate-400 font-normal">(uno per riga, oppure separati da virgola)</span></Label>
              <textarea
                data-testid="guarantee-aliases"
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                placeholder={"Es. RCA\nRC auto\nResponsabilità civile"}
                rows={3}
                className="w-full rounded-md border border-slate-200 bg-white text-sm px-3 py-2 font-mono"
              />
              <p className="text-[10px] text-slate-500 mt-1">Quando l&apos;AI estrae una garanzia con un nome diverso, viene automaticamente agganciata a questa voce se il nome corrisponde a uno degli alias qui sopra.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setForm(empty); setEditing(null); }}>Annulla</Button>
            <Button
              data-testid="save-guarantee-btn"
              onClick={submit}
              disabled={saving || !form.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {saving ? "Salvataggio…" : (editing ? "Salva" : "Aggiungi")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-guarantee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare la garanzia?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per eliminare:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">{deleteTarget?.name}</p>
            <p className="text-xs text-red-600">L&apos;operazione non può essere annullata.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button data-testid="confirm-delete-guarantee" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 15/07/2026 — Dialog "Importa da altra compagnia" */}
      <Dialog open={copyOpen} onOpenChange={(o) => { setCopyOpen(o); if (!o) setCopySource([]); }}>
        <DialogContent className="max-w-2xl" data-testid={`copy-guarantees-dialog-${companyId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Copy className="h-5 w-5" /> Importa garanzie da un&apos;altra compagnia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Compagnia di origine *</Label>
              <select
                data-testid="copy-source-company"
                className="w-full h-10 border border-slate-300 rounded-md px-2 text-sm"
                value={copyForm.source_company_id}
                onChange={(e) => loadCopySource(e.target.value)}
              >
                <option value="">— Seleziona compagnia —</option>
                {companiesList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            {copyForm.source_company_id && (
              <>
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>{copySource.length} garanzie disponibili · seleziona quelle da copiare (nessuna selezione = tutte)</span>
                  <label className="flex items-center gap-1.5 cursor-pointer" title="Salta garanzie con lo stesso nome già presenti in questa compagnia">
                    <input
                      type="checkbox"
                      className="accent-indigo-600"
                      checked={copyForm.skipDup}
                      onChange={(e) => setCopyForm((f) => ({ ...f, skipDup: e.target.checked }))}
                    />
                    Salta duplicati (per nome)
                  </label>
                </div>
                <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {copySource.length === 0 ? (
                    <div className="text-center text-sm text-slate-500 py-6">Nessuna garanzia configurata sulla compagnia di origine.</div>
                  ) : copySource.map((g) => {
                    const checked = copyForm.selectedIds.has(g.id);
                    return (
                      <label key={g.id} data-testid={`copy-source-item-${g.id}`} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-blue-50" : ""}`}>
                        <input
                          type="checkbox"
                          className="mt-1 accent-blue-600"
                          checked={checked}
                          onChange={() => {
                            setCopyForm((f) => {
                              const s = new Set(f.selectedIds);
                              if (s.has(g.id)) s.delete(g.id); else s.add(g.id);
                              return { ...f, selectedIds: s };
                            });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-800 flex items-center gap-2">
                            {g.sort_index != null && <span className="text-[10px] text-slate-500 tabular-nums">#{g.sort_index}</span>}
                            {g.name}
                          </div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                            <span>Imposte: <strong>{g.imposte_percent != null ? `${g.imposte_percent}%` : "—"}</strong></span>
                            <span>SSN: <strong>{g.ssn_percent != null ? `${g.ssn_percent}%` : "—"}</strong></span>
                            <span>Acq: <strong>{fmtEur(g.buy_price)}</strong></span>
                            <span>Ven: <strong className="text-emerald-700">{fmtEur(g.sell_price)}</strong></span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)} disabled={copying}>Annulla</Button>
            <Button
              data-testid="confirm-copy-guarantees"
              onClick={runCopy}
              disabled={copying || !copyForm.source_company_id || copySource.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {copying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Importa {copyForm.selectedIds.size > 0 ? `(${copyForm.selectedIds.size})` : "tutte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CompanyGuaranteesPanel;
